import { OpenInNew } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import type { ILanguageGeneral } from '@sharedInterfaces/Language/languageTexts/oftenUsed';
import Button from '@src/App/Components/Buttons/Button/Button';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import React from 'react';

interface EntityPageInformationProps {
    title: string;
    size?: 'normal' | 'full';
    helpText?: string;
    children: JSX.Element | string;
    icon?: React.ReactNode;
}

/**
 * Function to render Entity Page Information.
 *
 * @param {EntityPageInformationProps} title - The title of the entity page.
 * @param {EntityPageInformationProps} size - The size of the entity page.
 * @param {EntityPageInformationProps} helpText - The help text of the entity page.
 * @param {EntityPageInformationProps} children - The children of the entity page.
 *
 * @returns {JSX.Element} The rendered entity page information.
 */
export default function EntityPageInformation({ title, size, helpText, children, icon }: EntityPageInformationProps) {
    const theme = useTheme();
    const newSize = size ? size : 'normal';

    return (
        <Box
            sx={{
                ...(newSize === 'full'
                    ? {
                          gridColumnStart: 1,
                          gridColumnEnd: 'end',
                      }
                    : {}),
            }}
            title={helpText}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0.25 }}>
                {icon && (
                    <Box
                        sx={{
                            color: theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            '& svg': { fontSize: '1rem' },
                        }}
                    >
                        {icon}
                    </Box>
                )}
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: theme.palette.text.primary,
                    }}
                >
                    {title}:
                </Typography>
            </Box>
            <Box className="info-data">
                {typeof children === 'string' ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.5,
                        }}
                    >
                        {children}
                    </Typography>
                ) : (
                    children
                )}
            </Box>
        </Box>
    );
}

export function generateAcusitionEntityPageInformation(
    entity: { acquisition?: string; acquisitionLink?: string },
    langStrings: { oftenUsed: ILanguageGeneral },
) {
    return entity.acquisition || entity.acquisitionLink ? (
        <EntityPageInformation title={langStrings.oftenUsed.acquisition} size={'full'}>
            <>
                {entity.acquisition && (
                    <ExpandableText>
                        <FormatedTextInput value={entity.acquisition} readonly={true} />
                    </ExpandableText>
                )}
                {entity.acquisitionLink && (
                    <Box sx={{ display: 'inline-block' }}>
                        <Button
                            style={{ marginTop: 10 }}
                            text={langStrings.oftenUsed.howToLearn.toUpperCase()}
                            icon={<OpenInNew />}
                            onClick={() => {
                                window.open(entity.acquisitionLink, '_blank');
                            }}
                        />
                    </Box>
                )}
            </>
        </EntityPageInformation>
    ) : undefined;
}
